import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Typography, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";

import { FacebookPreview } from "./FacebookPreview";
import { InstagramPreview } from "./InstagramPreview";
import { LinkedInPreview } from "./LinkedinPreview";
import { TwitterPreview } from "./TwitterPreview";

export interface Platform {
  id: string;
  name: string;
  color: string;
  type: string;
  image: string;
}

// Update the MediaItem interface to include type information
export interface MediaItem {
  id: string;
  url: string;
  type?: "image" | "video";
  file?: File;
}

interface PreviewPaneProps {
  content: string;
  platforms: Platform[];
  deviceType: "mobile" | "desktop";
  media?: MediaItem[];
}

// Memoize platform preview components
const MemoizedTwitterPreview = React.memo(TwitterPreview);
const MemoizedFacebookPreview = React.memo(FacebookPreview);
const MemoizedInstagramPreview = React.memo(InstagramPreview);
const MemoizedLinkedInPreview = React.memo(LinkedInPreview);

// Create a separate component for the platform preview to avoid conditional hook calls
const PlatformPreview: React.FC<{ platform: Platform; content: string; media: MediaItem[] }> =
  React.memo(({ platform, content, media }) => {
    switch (platform.type.toLowerCase()) {
      case "twitter":
        return <MemoizedTwitterPreview content={content} media={media} />;
      case "facebook":
        return <MemoizedFacebookPreview content={content} media={media} />;
      case "instagram":
        return <MemoizedInstagramPreview content={content} media={media} />;
      case "linkedin":
        return <MemoizedLinkedInPreview content={content} media={media} />;
      default:
        return null;
    }
  });

// Add PropTypes for PlatformPreview
PlatformPreview.propTypes = {
  platform: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  }).isRequired,
  content: PropTypes.string.isRequired,
  media: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["image", "video"]),
      file: PropTypes.instanceOf(File),
    })
  ).isRequired,
};

PlatformPreview.displayName = "PlatformPreview";

// Create the component first
const PreviewPaneComponent: React.FC<PreviewPaneProps> = ({
  content,
  platforms,
  deviceType,
  media = [],
}) => {
  const [currentPlatformIndex, setCurrentPlatformIndex] = useState(0);

  // Move navigation handlers outside of conditional blocks
  const handleNextPlatform = React.useCallback(() => {
    setCurrentPlatformIndex((prevIndex) => (prevIndex + 1) % platforms.length);
  }, [platforms.length]);

  const handlePrevPlatform = React.useCallback(() => {
    setCurrentPlatformIndex((prevIndex) =>
      prevIndex === 0 ? platforms.length - 1 : prevIndex - 1
    );
  }, [platforms.length]);

  // Early return after hooks
  if (platforms.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 5, color: "grey.500" }}>
        Select a platform to see preview
      </Box>
    );
  }

  const currentPlatform = platforms[currentPlatformIndex];

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {/* Platform indicators */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mb: 2,
        }}
      >
        {platforms.map((platform, index) => (
          <Box
            key={platform.id}
            onClick={() => setCurrentPlatformIndex(index)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: index === currentPlatformIndex ? platform.color : "grey.300",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "scale(1.2)",
              },
            }}
          />
        ))}
      </Box>

      {/* Current platform preview */}
      <Box
        sx={
          deviceType === "mobile"
            ? { maxWidth: 320, mx: "auto", width: "100%" }
            : { maxWidth: 500, width: "100%", mx: "auto" }
        }
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
            boxShadow: 1,
            bgcolor: "common.white",
            overflow: "hidden",
            height: "100%",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: currentPlatform.color,
              color: "common.white",
              fontSize: "0.875rem",
              fontWeight: 500,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{currentPlatform.name} Preview</span>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="caption" sx={{ mr: 1, color: "white" }}>
                {currentPlatformIndex + 1} of {platforms.length}
              </Typography>
            </Box>
          </Box>
          <PlatformPreview platform={currentPlatform} content={content} media={media} />
        </Box>
      </Box>

      {/* Navigation arrows - only show if there's more than one platform */}
      {platforms.length > 1 && (
        <>
          <IconButton
            onClick={handlePrevPlatform}
            sx={{
              position: "absolute",
              left: { xs: 0, sm: -20 },
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": {
                bgcolor: "grey.100",
              },
            }}
            size="small"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            onClick={handleNextPlatform}
            sx={{
              position: "absolute",
              right: { xs: 0, sm: -20 },
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": {
                bgcolor: "grey.100",
              },
            }}
            size="small"
          >
            <NavigateNextIcon />
          </IconButton>
        </>
      )}
    </Box>
  );
};

// Define prop types for runtime validation
PreviewPaneComponent.propTypes = {
  content: PropTypes.string.isRequired,
  platforms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
    })
  ).isRequired,
  deviceType: PropTypes.oneOf(["mobile", "desktop"]).isRequired,
  media: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["image", "video"]),
      file: PropTypes.instanceOf(File),
    })
  ),
};

// Create the memoized version
export const PreviewPane = React.memo(PreviewPaneComponent);

// Add display name for better debugging
PreviewPane.displayName = "PreviewPane";
